/**
 * ActiveSessionBar — Persistent top bar shown on ALL pages when a software session is running.
 * 
 * Shows: "[App name] is running" [Open] [Stop]
 * Positioned at the very top of the layout (above TopBar).
 * Only visible when user is NOT on the /viewer/:slug page.
 * 
 * Reads session state from localStorage (set by AppViewerPage) and validates
 * against the backend /api/software/sessions endpoint.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { LinkSquare01Icon as ExternalLink, MonitorDotIcon as Monitor, SquareIcon as Square } from '@hugeicons/core-free-icons';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuthFetch } from '@/hooks/useAuthFetch';

interface ActiveSession {
  slug: string;
  sessionId: string;
  appName: string;
  startedAt: number;
}

export function ActiveSessionBar() {
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [stopping, setStopping] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const authFetch = useAuthFetch();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Don't show on viewer page (it has its own UI)
  const isViewerPage = location.pathname.startsWith('/viewer/');

  // Poll for active sessions
  useEffect(() => {
    const checkSession = async () => {
      // First check localStorage for known session
      const stored = localStorage.getItem('genofx:active_session');
      if (!stored) {
        setSession(null);
        return;
      }

      try {
        const parsed: ActiveSession = JSON.parse(stored);
        
        // Validate against backend
        const res = await authFetch('/api/software/sessions');
        if (!res.ok) return;
        const data = await res.json();
        const sessions = data.sessions || [];
        
        const match = sessions.find((s: any) =>
          s.id === parsed.sessionId && (s.status === 'running' || s.status === 'launching')
        );

        if (match) {
          setSession(parsed);
        } else {
          // Session no longer active — clean up
          localStorage.removeItem('genofx:active_session');
          setSession(null);
        }
      } catch {
        // On error, trust localStorage
        setSession(JSON.parse(stored));
      }
    };

    checkSession();
    pollRef.current = setInterval(checkSession, 10000); // Check every 10s

    // Also listen for storage changes (from other tabs or AppViewerPage)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'genofx:active_session') {
        if (e.newValue) {
          setSession(JSON.parse(e.newValue));
        } else {
          setSession(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('storage', handleStorage);
    };
  }, [authFetch]);

  const handleOpen = () => {
    if (session) {
      navigate(`/viewer/${session.slug}?session=${session.sessionId}`);
    }
  };

  const handleStop = async () => {
    if (!session) return;
    setStopping(true);
    try {
      const res = await authFetch('/api/software/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: session.sessionId }),
      });
      if (res.ok) {
        localStorage.removeItem('genofx:active_session');
        setSession(null);
        toast.success('Session stopped');
      }
    } catch {
      toast.error('Failed to stop session');
    } finally {
      setStopping(false);
    }
  };

  if (!session || isViewerPage) return null;

  return (
    <div className="w-full bg-emerald-600 dark:bg-emerald-700 text-white px-3 py-1 flex items-center justify-between z-40 shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <HugeiconsIcon icon={Monitor} className="h-3.5 w-3.5 shrink-0"  />
        <span className="text-xs font-medium truncate">
          {session.appName} is running
        </span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpen}
          className="h-6 px-2 text-[11px] text-white hover:bg-white/20"
        >
          <HugeiconsIcon icon={ExternalLink} className="h-3 w-3 mr-1"  /> Open
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleStop}
          disabled={stopping}
          className="h-6 px-2 text-[11px] text-white hover:bg-white/20"
        >
          <HugeiconsIcon icon={Square} className="h-3 w-3 mr-1"  /> Stop
        </Button>
      </div>
    </div>
  );
}
